/**
 * Getting a score in and out of the browser.
 *
 * Three formats, and they are not interchangeable. The editor's own JSON is the whole
 * document -- every id, every voice, everything the model holds -- and is what a score
 * should be kept in. MusicXML is the interchange format: it is what another program can
 * read, and a round trip through it is lossy in both directions, so it is offered as an
 * export and an import rather than as the thing you save to. MIDI is the sound and
 * nothing else: it carries pitch, time and loudness, so opening one is a transcription
 * rather than a read, and saving one throws the page away and keeps the performance.
 *
 * Reading a file can only go wrong in ways the user needs told about -- the wrong file, a
 * file from a newer editor, a MusicXML feature this importer drops -- so anything the
 * reader says comes back with the score rather than being swallowed.
 */

import { fromMidi, fromMusicXml, parse, serialize, toMidi, toMusicXml } from "@app/score-editor";
import type { Score } from "@app/score-editor";

/** What a file picker should accept: both of our formats, under every name they go by. */
export const SCORE_FILE_TYPES =
  ".json,.musicxml,.xml,.mid,.midi,application/json,application/vnd.recordare.musicxml+xml,text/xml,audio/midi";

/** A filename made from the title, so two saved scores do not collide in a downloads folder. */
function slug(title: string): string {
  const cleaned = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return cleaned === "" ? "score" : cleaned;
}

/**
 * What a Blob will take: text as it stands, bytes copied into a buffer of their own.
 *
 * The copy is not caution about the caller. A `Uint8Array` may be a view on memory shared
 * with a worker, which a Blob will not accept, and a fresh array is how that is ruled out
 * in the type rather than asserted past it.
 */
function bodyOf(body: string | Uint8Array): BlobPart {
  if (typeof body === "string") return body;
  const copy = new Uint8Array(body.length);
  copy.set(body);
  return copy;
}

/** Hand the browser a file to save. The object URL is released once the click is taken. */
export function download(name: string, type: string, body: string | Uint8Array): void {
  const url = URL.createObjectURL(new Blob([bodyOf(body)], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Save the document itself, which is the copy to keep. */
export function saveScore(score: Score): void {
  download(`${slug(score.meta.title)}.score.json`, "application/json", serialize(score, true));
}

/** Save what another program can open. */
export function exportMusicXml(score: Score): void {
  download(
    `${slug(score.meta.title)}.musicxml`,
    "application/vnd.recordare.musicxml+xml",
    toMusicXml(score),
  );
}

/** Save the performance, which is what a sequencer or a sampler wants. */
export function exportMidi(score: Score): void {
  download(`${slug(score.meta.title)}.mid`, "audio/midi", toMidi(score));
}

/** What was read, or what was wrong with it, in words fit to show. */
export interface FileRead {
  readonly score: Score | null;
  readonly note: string | null;
}

/** The four bytes every Standard MIDI File begins with. */
export function isMidi(data: Uint8Array): boolean {
  return data.length > 4 && data[0] === 0x4d && data[1] === 0x54 && data[2] === 0x68;
}

/**
 * Read a file by what is in it rather than by what it is called.
 *
 * An extension is a hint a user can get wrong; the bytes cannot be. MIDI names itself in
 * its first four, one of the other two formats is XML and the last is a JSON object.
 */
export async function readScoreFile(file: File): Promise<FileRead> {
  const data = new Uint8Array(await file.arrayBuffer());
  if (isMidi(data)) return readMidiFile(data);
  return readTextFile(new TextDecoder().decode(data));
}

/**
 * Open a MIDI file, saying plainly that what comes back was worked out rather than read.
 *
 * The note is not a warning about something that went wrong: every MIDI file is opened
 * this way, because the format holds no bar lines, no note values, no staves, no voices and
 * no spelling. Saying so once is what keeps a player from thinking the editor lost something
 * the file had.
 */
export function readMidiFile(data: Uint8Array): FileRead {
  const result = fromMidi(data);
  if (!result.ok) return { score: null, note: result.reason };
  const written =
    "Written down from MIDI: the bars, the note values, the staves, the voices and the spelling are the editor's reading of what was played, not the file's own.";
  return {
    score: result.score,
    note: result.warnings.length === 0 ? written : `${written} ${result.warnings.join("; ")}.`,
  };
}

function readTextFile(source: string): FileRead {
  if (source.trimStart().startsWith("<")) {
    const result = fromMusicXml(source);
    if (!result.ok) return { score: null, note: result.reason };
    const warnings = result.warnings;
    return {
      score: result.score,
      note:
        warnings.length === 0
          ? null
          : `Opened, with what could not be read: ${warnings.join("; ")}`,
    };
  }

  const result = parse(source);
  if (!result.ok) return { score: null, note: result.reason };
  return {
    score: result.file.score,
    note: result.migrated ? "Opened a file from an older version of the editor." : null,
  };
}
